import { useTranslation } from 'react-i18next';
import { LEGACY_STATUS_TABS, type LegacyOrderCountInfo } from '@/entities/legacy-order';

/**
 * The status pills above the orders table, reproducing the old `TabContainer`
 * + `Tab`: a white 24px-radius bar holding pills that are 40px-radius,
 * `12px/8px` padded, grey when idle and solid black with white text when
 * selected. Each carries a leading 8px dot in its status color — which turns
 * white on the selected pill, since the black fill would swallow it.
 *
 * The bar scrolls horizontally on narrow viewports (there are ten pills), as
 * the original did.
 */
interface LegacyStatusFilterProps {
  selected: string;
  counts: LegacyOrderCountInfo | undefined;
  onSelect: (id: string) => void;
}

export function LegacyStatusFilter({
  selected,
  counts,
  onSelect,
}: LegacyStatusFilterProps) {
  const { t } = useTranslation('legacy');

  return (
    // `lg-xscroll` for the same reason as the grid: ten pills overflow even at
    // FHD, and an overlay scrollbar that is invisible at rest makes the
    // trailing pills ("Ожидание оплаты курьером") look absent rather than
    // scrollable. It owns the `overflow-x`.
    <div className="lg-xscroll flex w-full items-center gap-1 rounded-[24px] bg-[var(--lg-surface)] p-1">
      {LEGACY_STATUS_TABS.map((tab) => {
        const isSelected = tab.id === selected;
        const count = counts?.[tab.countKey as keyof LegacyOrderCountInfo] ?? 0;

        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onSelect(tab.id)}
            aria-pressed={isSelected}
            // The visible label is split across two spans (dot + text), so
            // without this the button exposes no usable accessible name.
            aria-label={`${t(tab.labelKey)} (${count})`}
            className={[
              'flex shrink-0 cursor-pointer items-center gap-2 rounded-[40px] px-3 py-2',
              'text-[16px] leading-[19px] font-medium whitespace-nowrap transition-colors',
              isSelected
                ? 'bg-[var(--lg-fg)] text-[var(--lg-surface)]'
                : 'text-[var(--lg-grey-700)] hover:bg-[var(--lg-grey-100)]',
            ].join(' ')}
          >
            <span
              className="size-2 shrink-0 rounded-full"
              style={{
                backgroundColor: isSelected ? 'var(--lg-surface)' : `var(${tab.dotVar})`,
              }}
            />
            <span>
              {/* Counts render RAW — the old `TabContainer` printed the number
                  with no thousands grouping. */}
              {t(tab.labelKey)} ({count})
            </span>
          </button>
        );
      })}
    </div>
  );
}
